import * as React from 'react';
import { observer } from 'src/utils/mobx-react';
import {
    ResponsibleInfo,
    SingleItem,
    SingleItemLink,
    LogoGambleAwareWrapper,
    LogoGamstopWrapper,
    Logo18PlusWrapper,
    LogoProblemGamblingWrapper,
    LogoGamblingCommissionWrapper,
    LogoRow,
    LogoIbasWrapper,
    Plus18Wrapper,
    Plus18Text,
    LogoEcograWrapper,
} from './ResponsibleGambling.style';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { FooterHeader } from 'src/domains/layouts/webview/modules/layoutSkeleton/footer/content/FooterContent.style';
import problemGambling from 'src/domains/layouts/webview/modules/layoutSkeleton/footer/responsibleGambling/assets/problem-gambling.webp';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { LogoGamblingTherapy } from 'src/domains/layouts/shared/assets/logotypes/responsible-gambling/LogoGamblingTherapy';
import { ResponsibleGamblingInfo } from 'src/domains/layouts/webview/modules/layoutSkeleton/footer/responsibleGambling/ResponsibleGamblingInfo';
import {
    LogoGamblingHelplineWrapper,
    LogoTakeTimeToThinkWrapper,
} from 'src/domains/layouts/webview/modules/layoutSkeleton/footer/responsibleGambling/ResponsibleGamblingDefault.style';
import EcorgaLogo from 'src/domains/layouts/shared/assets/logotypes/responsible-gambling/ecogra.png';

export const ResponsibleGamblingDefault = observer('ResponsibleGamblingDefault', (): JSX.Element => {
    const common = useCommon();
    const {
        problemGambling: problemGamblingFlag,
        depositLimitsType,
        isSaferGambling,
        footerSaferGamblingLogos: { ibas, ecogra, takeTimeToThink, gamblingTherapy, gamblingComissionRGLogo },
    } = ConfigComponents.get(common).config;
    const languagesState = LanguagesState.get(common);

    const ibasLabel = languagesState.getTranslation(
        'footer.responsible.gambling.ibas.label',
        'read more about ibas UK'
    );
    const ecograLabel = languagesState.getTranslation(
        'footer.responsible.gambling.ecogra.label',
        'read more about eCOGRA'
    );
    const gamblingCommissionLabel = languagesState.getTranslation(
        'footer.responsible.gambling.gamblingcomission.label',
        'read more about gambling comission'
    );
    const gambleAwareLabel = languagesState.getTranslation(
        'footer.responsible.gambling.gamble-aware.label',
        'read more about be gamble aware'
    );
    const gamstopLabel = languagesState.getTranslation(
        'footer.responsible.gambling.gamstop.label',
        'read more about gamstop.co.uk'
    );
    const problemgamblingLabel = languagesState.getTranslation(
        'footer.responsible.gambling.problemgambling.label',
        'read more about problemgambling.ie'
    );
    const gamCareHelplineLabel = languagesState.getTranslation(
        'footer.responsible.gambling.gamcarehelpline.label',
        'read more at gamcare.org.uk'
    );
    const takeTimeToThinkLabel = languagesState.getTranslation(
        'footer.responsible.gambling.taketimetothink.label',
        'read more at taketimetothink.co.uk'
    );
    const gamblingTherapyLabel = languagesState.getTranslation(
        'footer.responsible.gambling.gamblingtherapy.label',
        'read more at gamblingtherapy.org'
    );

    return (
        <React.Fragment>
            <FooterHeader>
                {depositLimitsType === 'calendarType' || isSaferGambling.available ? (
                    <I18n
                        langKey='footer.safer.gambling'
                        defaultText='Safer Gambling'
                    />
                ) : (
                    <I18n
                        langKey='footer.responsible.gambling'
                        defaultText='Responsible Gambling'
                    />
                )}
            </FooterHeader>

            <Plus18Wrapper>
                <SingleItem data-test='18-plus-logo'>
                    <Logo18PlusWrapper />
                </SingleItem>
                <Plus18Text>
                    <I18n
                        langKey='footer.underage_gambling'
                        defaultText='Underage Gambling is an offence'
                    />
                </Plus18Text>
            </Plus18Wrapper>

            {ibas === true ? (
                <LogoRow>
                    <SingleItem>
                        <SingleItemLink
                            aria-label={ibasLabel}
                            to='https://www.ibas-uk.com/'
                            dataTest='responsible-gambling-ibas'
                        >
                            <LogoIbasWrapper />
                        </SingleItemLink>
                    </SingleItem>
                </LogoRow>
            ) : null}

            {ecogra === true ? (
                <LogoRow>
                    <SingleItem>
                        <SingleItemLink
                            aria-label={ecograLabel}
                            to='https://ecogra.org/alternative-dispute-resolution/'
                            dataTest='responsible-gambling-ecogra'
                        >
                            <LogoEcograWrapper
                                src={EcorgaLogo}
                                alt='Yaspa'
                            />
                        </SingleItemLink>
                    </SingleItem>
                </LogoRow>
            ) : null}

            <LogoRow
                isCentered={true}
                isWrap={true}
            >
                <SingleItem>
                    <SingleItemLink
                        aria-label={gamstopLabel}
                        to='https://www.gamstop.co.uk/'
                        dataTest='responsible-gambling-gamstop'
                    >
                        <LogoGamstopWrapper />
                    </SingleItemLink>
                </SingleItem>
                {gamblingComissionRGLogo === true ? (
                    <SingleItem>
                        <SingleItemLink
                            aria-label={gamblingCommissionLabel}
                            to='https://www.gamblingcommission.gov.uk/'
                            dataTest='responsible-gambling-gamblingcomission'
                        >
                            <LogoGamblingCommissionWrapper />
                        </SingleItemLink>
                    </SingleItem>
                ) : null}
            </LogoRow>

            {problemGamblingFlag === true ? (
                <LogoRow>
                    <SingleItem>
                        <SingleItemLink
                            aria-label={problemgamblingLabel}
                            to='https://www.problemgambling.ie/'
                        >
                            <LogoProblemGamblingWrapper
                                imgBase={{ src: problemGambling }}
                                alt='Problem gambling logo'
                            />
                        </SingleItemLink>
                    </SingleItem>
                </LogoRow>
            ) : null}

            <LogoRow>
                <SingleItem>
                    <SingleItemLink
                        aria-label={gambleAwareLabel}
                        to='https://www.gambleaware.org/'
                        dataTest='responsible-gambling-gambleaware'
                    >
                        <LogoGambleAwareWrapper />
                    </SingleItemLink>
                </SingleItem>
            </LogoRow>

            {isSaferGambling.available && (
                <>
                    <LogoRow>
                        <SingleItem>
                            <SingleItemLink
                                aria-label={gamCareHelplineLabel}
                                to='https://www.gamcare.org.uk/get-support/talk-to-us-now/'
                                dataTest='responsible-gambling-gamcare-helpline'
                            >
                                <LogoGamblingHelplineWrapper />
                            </SingleItemLink>
                        </SingleItem>
                    </LogoRow>

                    {takeTimeToThink === true ? (
                        <LogoRow>
                            <SingleItem>
                                <SingleItemLink
                                    aria-label={takeTimeToThinkLabel}
                                    to='https://www.taketimetothink.co.uk/'
                                    dataTest='responsible-gambling-take-time-to-think'
                                >
                                    <LogoTakeTimeToThinkWrapper />
                                </SingleItemLink>
                            </SingleItem>
                        </LogoRow>
                    ) : null}

                    {gamblingTherapy === true ? (
                        <LogoRow>
                            <SingleItem>
                                <SingleItemLink
                                    aria-label={gamblingTherapyLabel}
                                    to='https://gamblingtherapy.org/'
                                    dataTest='responsible-gambling-therapy'
                                >
                                    <LogoGamblingTherapy />
                                </SingleItemLink>
                            </SingleItem>
                        </LogoRow>
                    ) : null}
                </>
            )}

            <ResponsibleInfo data-test='responsible-gambling-section-responsible-gambling-link'>
                <ResponsibleGamblingInfo />
            </ResponsibleInfo>
        </React.Fragment>
    );
});
